import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle } from 'lucide-react';
import { authApi, ApiError } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword]   = useState('');
  const [confirm, setConfirm]     = useState('');
  const [loading, setLoading]     = useState(false);
  const [done, setDone]           = useState(false);
  const [error, setError]         = useState('');

  // No token in URL — show an error immediately
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
           style={{ background: '#1e3a5f' }}>
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src="/logo.png?v=4" alt="VantixMT"
                 className="w-40 sm:w-56 object-contain drop-shadow-xl" />
          </div>
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-12 text-center">
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
                <XCircle size={36} className="text-red-500" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid reset link</h2>
            <p className="text-slate-500 text-sm mb-8">
              No reset token was found. Please use the link from your email.
            </p>
            <Link to="/forgot-password" className="text-brand-600 hover:underline text-sm font-medium">
              Request a new link
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
           style={{ background: '#1e3a5f' }}>
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-8">
            <img src="/logo.png?v=4" alt="VantixMT"
                 className="w-40 sm:w-56 object-contain drop-shadow-xl" />
          </div>
          <div className="bg-white rounded-2xl shadow-2xl px-8 py-12 text-center">
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center">
                <CheckCircle size={36} className="text-green-500" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-slate-900 mb-2">Password reset!</h2>
            <p className="text-slate-500 text-sm mb-8">
              Your password has been updated. You can now sign in with your new password.
            </p>
            <Link
              to="/login"
              className="inline-block px-6 py-2.5 bg-brand-600 text-white font-semibold rounded-lg hover:bg-brand-700 transition-colors text-sm"
            >
              Sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4"
         style={{ background: '#1e3a5f' }}>
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.png?v=4" alt="VantixMT"
               className="w-40 sm:w-56 object-contain drop-shadow-xl" />
        </div>
        <div className="bg-white rounded-2xl shadow-2xl px-8 py-10">
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-slate-900">Set new password</h1>
            <p className="text-sm text-slate-500 mt-1">Choose a strong password for your account.</p>
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <Input
              label="New Password *"
              type="password"
              placeholder="At least 8 characters"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoFocus
            />
            <Input
              label="Confirm Password *"
              type="password"
              placeholder="••••••••"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              required
            />

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</p>
            )}

            <Button type="submit" className="w-full mt-2" disabled={loading}>
              {loading ? 'Saving…' : 'Reset password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
